// /for-barbers, a focused funnel for barbershop owners.
//
// Barbers convert differently to braiders or aestheticians: walk-in
// culture, 30-45 minute services, repeat clientele, and chair-rental
// arrangements with multiple barbers per shop. This page leads with the
// language and value props that actually matter to them, then funnels
// straight into provider registration with the `barbering_grooming`
// pillar pre-selected so the Categories step is one-tap on arrival.
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import {
  Scissors, ArrowRight, Clock, Users, Wallet, ShieldCheck,
  Calendar, Bell, Star, Crown,
} from 'lucide-react';
import Header from '../components/Header';
import { Footer } from '../components/landing/Footer';
import { SEO, buildWebsiteSchema } from '../components/SEO';
import { useFunnelPageview } from '../lib/funnels';

const VALUE_PROPS = [
  {
    icon: Clock,
    title: 'Built for short, dense days',
    body:
      'A 30-minute Skin Fade or a 20-minute Beard Line-up slots in cleanly. Your calendar packs without overlap, and clients see real-time gaps so the chair never sits empty.',
  },
  {
    icon: Users,
    title: 'Multi-chair, multi-barber friendly',
    body:
      'Run a shop with three chairs and four barbers? Each barber gets their own Zuriya link, their own availability and their own payouts. Your shop page lists them all under one roof.',
  },
  {
    icon: Bell,
    title: 'Walk-ins, online, on your terms',
    body:
      'Take online bookings, walk-ins, or both. Toggle a chair to "walk-in only" for an afternoon when the queue picks up, no awkward double-bookings.',
  },
  {
    icon: Wallet,
    title: 'UK payouts, weekly, no chasing cash',
    body:
      'Card payments clear into your UK bank every Monday once your balance hits £100. No more "card machine is down today, can you tap again". Stripe handles the rails, we handle the paperwork.',
  },
];

const FAQ = [
  {
    q: 'What does it actually cost?',
    a: 'Free plan: 15% commission on new clients, 0% on repeat clients. Pro plan (£25/month): 8% on new clients, 0% on repeat. Card processing is 1.75% + 20p (Free) or 1.55% + 20p (Pro). No setup fee, cancel any time.',
  },
  {
    q: 'I rent a chair, can I still join?',
    a: 'Yes. Each barber signs up individually with their own Zuriya link and bank details. If the shop owner wants a "house" profile too, they can list the shop as the brand and link the barbers below, you tell us how you want to be discovered.',
  },
  {
    q: 'My clients just walk in, do I need bookings?',
    a: 'Not always. Mark any time block as "walk-in only" and Zuriya will hide booking buttons for that window. Some shops use online bookings only for Saturday rush and leave weekdays open for walk-ins, totally up to you.',
  },
  {
    q: 'What if a client no-shows?',
    a: 'You set the deposit. £5, £10, 50% of the cut, your call. If a client cancels too late or doesn\'t turn up, the deposit is retained per your policy. No-shows are exactly why most barbers go Pro.',
  },
  {
    q: 'How long does onboarding take?',
    a: 'About 6 minutes if you have your bank details and a profile photo ready. We pre-fill standard services (Skin Fade, Beard Line-up, Father & Son Cut and more) so you just untick what you don\'t offer.',
  },
];

export default function ForBarbers() {
  const { user, betaConfig } = useAuth();
  const betaMode = !!betaConfig?.beta_mode;
  // Stamp the visit so the admin dashboard can compute conversion rate
  // against signups attributed to ?from=for_barbers.
  useFunnelPageview({ path: '/for-barbers', pillar: 'barbering_grooming', source: 'for_barbers' });
  const registerHref =
    user?.role === 'provider'
      ? '/provider/onboarding?category=barbering_grooming'
      : '/register?role=provider&category=barbering_grooming&from=for_barbers';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title="For Barbers, List Your Barbershop on Zuriya"
        description="The UK booking platform built for multi-cultural barbershops. Walk-ins, online appointments, multi-chair payouts, weekly to your UK bank. Free to join."
        jsonLd={buildWebsiteSchema()}
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-[#1A1A1A] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-20 lg:py-28">
          <div className="max-w-3xl">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-semibold mb-6"
              data-testid="for-barbers-overline"
            >
              <Scissors className="h-4 w-4" />
              For Barbershops, UK-wide
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-white mb-6"
              data-testid="for-barbers-hero"
            >
              List your barbershop.<br />
              <span className="text-[#D4AF37]">Fill the chair, not the inbox.</span>
            </h1>
            <p className="text-lg text-stone-400 mb-10 max-w-2xl leading-relaxed">
              Zuriya is the UK booking platform built for multi-cultural barbers. Walk-ins, online bookings, deposits to kill no-shows, and weekly payouts straight to your bank.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={registerHref}>
                <Button
                  className="btn-primary h-14 px-8 text-base rounded-full"
                  data-testid="for-barbers-primary-cta"
                >
                  Start Listing Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button
                  variant="outline"
                  className="h-14 px-8 text-base rounded-full border-white/30 text-white hover:bg-white hover:text-[#1A1A1A]"
                  data-testid="for-barbers-secondary-cta"
                >
                  How it works
                </Button>
              </a>
            </div>
            <p className="mt-6 text-sm text-stone-500">
              Free plan available, no card needed. Set up in under 10 minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-20 bg-white" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="overline mb-2 block">Why Zuriya for Barbers</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-3">
              Built for how a barbershop actually runs
            </h2>
            <p className="text-stone-500 leading-relaxed">
              Most booking platforms were designed for hour-long salon visits. We built Zuriya so a Skin Fade fits in 30 minutes, a queue of regulars feels native, and one shop can host five barbers without anyone fighting over the diary.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {VALUE_PROPS.map((vp) => (
              <div
                key={vp.title}
                className="card-surface p-6 sm:p-7 border border-stone-100"
                data-testid={`for-barbers-prop-${vp.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#F9F6F0] flex items-center justify-center mb-4">
                  <vp.icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">{vp.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{vp.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-12 bg-[#F9F6F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <ShieldCheck className="h-6 w-6 text-[#D4AF37] mb-2" />
              <p className="text-sm font-medium text-[#1A1A1A]">Stripe-secured payments</p>
            </div>
            <div className="flex flex-col items-center">
              <Calendar className="h-6 w-6 text-[#D4AF37] mb-2" />
              <p className="text-sm font-medium text-[#1A1A1A]">Real-time availability</p>
            </div>
            <div className="flex flex-col items-center">
              <Star className="h-6 w-6 text-[#D4AF37] mb-2" />
              <p className="text-sm font-medium text-[#1A1A1A]">Reviews from real cuts</p>
            </div>
            <div className="flex flex-col items-center">
              <Crown className="h-6 w-6 text-[#D4AF37] mb-2" />
              <p className="text-sm font-medium text-[#1A1A1A]">HMRC-ready exports</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white" id="pricing">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {betaMode ? (
            <>
              <div className="text-center mb-12">
                <span className="inline-block text-[10px] font-medium tracking-widest uppercase text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-2 py-1 rounded-full mb-3">
                  Beta phase
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-3">
                  Founding Provider (Beta)
                </h2>
                <p className="text-stone-500 max-w-xl mx-auto">
                  Zuriya is currently in its beta phase while we work closely with our community to build the best possible platform for multi-cultural beauty professionals across the UK.
                </p>
              </div>
              <div className="max-w-md mx-auto">
                <div
                  className="relative card-surface p-8 rounded-xl border-2 border-[#D4AF37]"
                  data-testid="for-barbers-pricing-beta"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#1A1A1A] text-xs font-bold px-3 py-1 rounded-full">
                    FOUNDING PROVIDER
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">Founding Provider (Beta)</h3>
                  </div>
                  <p className="text-3xl font-bold text-[#1A1A1A] mb-4">
                    &pound;0<span className="text-base font-normal text-stone-400">/month</span>
                  </p>
                  <ul className="space-y-2 text-sm text-stone-600">
                    {[
                      'Full access to all provider features',
                      'No subscription fees',
                      `${betaConfig?.provider_fee_percent}% provider fee on new marketplace clients, Stripe costs included`,
                      '0% fee on repeat clients and bookings through your own link (Stripe processing applies)',
                      'Clients pay no booking fee',
                      'Help shape future platform features through feedback',
                      'Direct access to beta improvements and updates',
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-0.5">&#10003;</span>{f}
                      </li>
                    ))}
                  </ul>
                  <Link to={registerHref}>
                    <Button
                      className="w-full mt-6 btn-primary"
                      data-testid="for-barbers-beta-cta"
                    >
                      Apply as a Founding Provider
                    </Button>
                  </Link>
                  <p className="text-[11px] text-stone-400 mt-3 text-center">
                    All applications are reviewed by the Zuriya team.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-12">
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-3">
                  Simple pricing, no surprise fees
                </h2>
                <p className="text-stone-500 max-w-xl mx-auto">
                  Choose Free or Pro. Switch any time. Free is enough to run a one-chair shop, Pro pays for itself with two no-show recoveries a month.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free Tier */}
            <div className="card-surface p-7 rounded-xl" data-testid="for-barbers-pricing-free">
              <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-1">Free</h3>
              <p className="text-3xl font-bold text-[#1A1A1A] mb-4">
                £0<span className="text-base font-normal text-stone-400">/month</span>
              </p>
              <div className="space-y-2 mb-6 text-sm text-stone-600">
                <p className="font-medium text-[#1A1A1A]">15% commission on new clients</p>
                <p>0% commission on repeat clients</p>
                <p>1.75% + 20p card processing</p>
              </div>
              <ul className="space-y-2 text-sm text-stone-600">
                {['Unlimited services', 'Booking calendar', 'Messaging with booked clients', 'Reviews and ratings', 'Weekly UK bank payouts', 'Basic analytics'].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="text-[#D4AF37] mt-0.5">&#10003;</span>{f}
                  </li>
                ))}
              </ul>
              <Link to={registerHref}>
                <Button
                  variant="outline"
                  className="w-full mt-6 border-[#E6E4DD]"
                  data-testid="for-barbers-free-cta"
                >
                  Start Free
                </Button>
              </Link>
            </div>
            {/* Pro Tier */}
            <div
              className="relative card-surface p-7 rounded-xl border-2 border-[#D4AF37]"
              data-testid="for-barbers-pricing-pro"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#1A1A1A] text-xs font-bold px-3 py-1 rounded-full">
                BUSIEST SHOPS PICK THIS
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Crown className="h-5 w-5 text-[#D4AF37]" />
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">Pro</h3>
              </div>
              <p className="text-3xl font-bold text-[#1A1A1A] mb-4">
                £25<span className="text-base font-normal text-stone-400">/month</span>
              </p>
              <div className="space-y-2 mb-6 text-sm text-stone-600">
                <p className="font-medium text-[#D4AF37]">8% commission on new clients</p>
                <p>0% commission on repeat clients</p>
                <p>1.55% + 20p card processing</p>
              </div>
              <ul className="space-y-2 text-sm text-stone-600">
                {['Everything in Free', 'Prospect messaging (reply to enquiries)', 'Smart waitlists (auto-fill gaps)', 'SMS booking + reminder alerts', 'HMRC-ready VAT & sales reports', 'Advanced analytics', 'Priority support'].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="text-[#D4AF37] mt-0.5">&#10003;</span>{f}
                  </li>
                ))}
              </ul>
              <Link to={registerHref}>
                <Button
                  className="w-full mt-6 btn-primary"
                  data-testid="for-barbers-pro-cta"
                >
                  Start with Pro
                </Button>
              </Link>
            </div>
          </div>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-10 text-center">
            Questions from the chair
          </h2>
          <div className="space-y-4" data-testid="for-barbers-faq">
            {FAQ.map((item) => (
              <details
                key={item.q}
                className="group card-surface p-5 rounded-lg border border-stone-100"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-medium text-[#1A1A1A] text-base">{item.q}</h3>
                  <span className="text-[#D4AF37] text-xl font-light group-open:rotate-45 transition-transform duration-200">
                    +
                  </span>
                </summary>
                <p
                  className="mt-3 text-stone-500 text-sm leading-relaxed"
                  data-answer={item.a}
                >
                  {item.q === 'What does it actually cost?' && betaMode
                    ? `Zuriya is in Founding Provider Beta right now. There's no subscription fee and clients pay no booking fee. On new marketplace clients, a ${betaConfig?.provider_fee_percent}% fee comes out of your payout and covers Stripe, so a £100 booking pays you £${100 - (betaConfig?.provider_fee_percent || 0)}. Repeat clients and bookings through your own link carry no fee (standard Stripe processing applies). When we exit beta the paid plans return, existing beta providers will get plenty of notice.`
                    : item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Scissors className="h-10 w-10 text-[#D4AF37] mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white mb-4">
            Ready to fill the chair?
          </h2>
          <p className="text-stone-400 mb-8 max-w-xl mx-auto">
            Set up your shop in under 10 minutes. We pre-fill the standard menu (Skin Fade, Beard Line-up, Father & Son Cut and more), so you can be open for bookings tonight.
          </p>
          <Link to={registerHref}>
            <Button
              className="btn-primary h-14 px-8 text-base rounded-full"
              data-testid="for-barbers-final-cta"
            >
              Start Listing Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
